import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LogIn, User } from 'lucide-react';
import NavLink from './NavLink';
import { useDashboard } from '@/hooks/useDashboard';

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileNav: React.FC<MobileNavProps> = ({ isOpen, onClose }) => {
  const { userData, isAdmin, handleSignOut, isAuthCheckComplete } = useDashboard();
  const isAuthenticated = !!userData;

  if (!isOpen) return null;

  return (
    <div className="md:hidden bg-white py-4 shadow-lg">
      <div className="container-wide flex flex-col space-y-4">
        <div className="border-b border-gray-100 pb-2 mb-2">
          <span className="font-medium text-itmgmt-dark-blue">Services</span>
          <div className="pl-4 mt-2 space-y-2">
            <Link 
              to="/services/managed-it" 
              className="text-itmgmt-dark-gray hover:text-itmgmt-medium-blue font-medium py-1 block transition-colors"
              onClick={onClose}
              aria-label="Learn more about our comprehensive Managed IT Services"
              title="Explore our Managed IT Services solutions"
            >
              Explore Managed IT Services
            </Link>
            <Link 
              to="/services/cybersecurity" 
              className="text-itmgmt-dark-gray hover:text-itmgmt-medium-blue font-medium py-1 block transition-colors"
              onClick={onClose}
              aria-label="Learn more about our Cybersecurity solutions"
              title="Protect your business with our Cybersecurity solutions"
            >
              Cybersecurity Protection
            </Link>
            <Link 
              to="/services/network-infrastructure" 
              className="text-itmgmt-dark-gray hover:text-itmgmt-medium-blue font-medium py-1 block transition-colors"
              onClick={onClose}
              aria-label="Learn more about our Network & Infrastructure services"
              title="Network & Infrastructure solutions for your business"
            >
              Network & Infrastructure Services
            </Link>
            <Link 
              to="/services/cloud-solutions" 
              className="text-itmgmt-dark-gray hover:text-itmgmt-medium-blue font-medium py-1 block transition-colors"
              onClick={onClose}
              aria-label="Learn more about our Cloud Solutions"
              title="Cloud Solutions to enhance your business operations"
            >
              Cloud Solutions & Migration
            </Link>
            <Link 
              to="/services/project-consulting" 
              className="text-itmgmt-dark-gray hover:text-itmgmt-medium-blue font-medium py-1 block transition-colors"
              onClick={onClose}
              aria-label="Learn more about our IT Strategy & Project Consulting"
              title="Strategic IT consulting to align technology with your business goals"
            >
              IT Strategy & Consulting
            </Link>
            <Link 
              to="/services/specialty-services" 
              className="text-itmgmt-dark-gray hover:text-itmgmt-medium-blue font-medium py-1 block transition-colors"
              onClick={onClose}
              aria-label="Learn more about our Specialty IT Services"
              title="Specialized IT services tailored to your unique requirements"
            >
              Specialty IT Services
            </Link>
            <Link 
              to="/services/residential" 
              className="text-itmgmt-dark-gray hover:text-itmgmt-medium-blue font-medium py-1 block transition-colors"
              onClick={onClose}
              aria-label="Learn more about our Residential IT Support Services"
              title="Professional IT support for home users and families"
            >
              Residential IT Support
            </Link>
          </div>
        </div>
        
        <NavLink 
          sectionId="why-choose-us" 
          onClick={onClose}
          className="text-itmgmt-dark-gray hover:text-itmgmt-medium-blue font-medium py-2 transition-colors text-left"
        >
          Why Choose Us
        </NavLink>
        <NavLink 
          sectionId="industries" 
          onClick={onClose}
          className="text-itmgmt-dark-gray hover:text-itmgmt-medium-blue font-medium py-2 transition-colors text-left"
        >
          Industries
        </NavLink>
        
        <Link 
          to="/blog" 
          onClick={onClose}
          className="text-itmgmt-dark-gray hover:text-itmgmt-medium-blue font-medium py-2 transition-colors text-left block"
        >
          Blog
        </Link>
        
        <Button asChild className="button-primary w-full">
          <Link to="/book" onClick={onClose}>Book a Consultation</Link>
        </Button>
        
        {isAuthCheckComplete && (
          isAuthenticated ? (
            <>
              <Button 
                asChild 
                variant="outline" 
                className="text-itmgmt-dark-blue border-itmgmt-dark-blue hover:bg-itmgmt-medium-blue/10 w-full"
              >
                <Link to={isAdmin ? "/admin" : "/dashboard"} onClick={onClose}>
                  <User className="mr-2 h-4 w-4" />
                  {isAdmin ? "Admin Dashboard" : "Dashboard"}
                </Link>
              </Button>
              <Button 
                variant="ghost" 
                className="text-itmgmt-dark-blue hover:bg-itmgmt-medium-blue/10 w-full"
                onClick={() => {
                  handleSignOut();
                  onClose();
                }}
              >
                Log Out
              </Button>
            </>
          ) : (
            <Button 
              asChild 
              variant="outline" 
              className="text-itmgmt-dark-blue border-itmgmt-dark-blue hover:bg-itmgmt-medium-blue/10 w-full"
            >
              <Link to="/login" onClick={onClose}><LogIn className="mr-2 h-4 w-4" /> Login</Link>
            </Button>
          )
        )}
      </div>
    </div>
  );
};

export default MobileNav;
